import { observer } from 'mobx-react';
import * as React from 'react';

import { DbKey } from '../adl-gen/common/db';
import {
  AppClient,
  ApplicationRole,
  // AppUser,
  Quiz,
  QuizType,
  UserQuiz
} from '../adl-gen/totalinteraction/futurefit/db';
import { AssessmentStatus } from '../adl-gen/totalinteraction/futurefit/frontend';
import { NotEnabled, UserUploadData } from '../adl-gen/totalinteraction/futurefit/requests';
import { UiConfig } from '../adl-gen/totalinteraction/futurefit/uiconfig';
import { AppUserStore, AppUserUploadStore } from '../admin/stores';
import { FormMode } from '../admin/ui/common/form';
import { AdminPageLayoutFn } from '../admin/ui/common/page-layout/admin-page-layout';
import { AppClientDetailScreen } from '../admin/ui/screens/appclient-detail-screen/appclient-detail-screen';
import { AppClientScreen } from '../admin/ui/screens/appclient-screen/appclient-screen';
import { AppUserUploadResultScreen } from '../admin/ui/screens/appuser-upload-screen/appuser-upload-result-screen';
import { AppUserUploadScreen } from '../admin/ui/screens/appuser-upload-screen/appuser-upload-screen';
import { CompetencyDetailScreen } from '../admin/ui/screens/competency-detail-screen/competency-detail-screen';
import { CompetencySkillScreen } from '../admin/ui/screens/competency-skill-screen/competency-skill-screen';
import { SelfAssessmentQuestionScreen } from '../ui/screens/self-assessment-question-screen/self-assessment-question-screen';
import { TrainingOfferingDetailScreen } from "../admin/ui/screens/training-offering-detail-screen/training-offering-detail-screen";
import { UserGroupDetailsScreen } from '../admin/ui/screens/usergroup-detail-screen/usergroup-detail-screen';
import { UserGroupScreen } from '../admin/ui/screens/usergroup-screen/usergroup-screen';
import { QuestionAnswerDetailScreen } from '../admin/ui/screens/question-answer-detail-screen/question-answer-detail-screen';
import { QuestionScreen } from '../admin/ui/screens/question-screen/question-screen';
import { QuizAdminScreen } from '../admin/ui/screens/quiz-admin-screen/quiz-admin-screen';
import { RoleBuilderScreen } from '../admin/ui/screens/role-builder-screen/role-builder-screen';
import { SkillDetailScreen } from '../admin/ui/screens/skill-detail-screen/skill-detail-screen';
import { AppUserDetailScreen } from '../admin/ui/screens/appuser-detail-screen/appuser-detail-screen';
import { AppUserScreen } from '../admin/ui/screens/appuser-screen/appuser-screen';
import { WorkRoleDetailScreen } from '../admin/ui/screens/workrole-detail-screen/workrole-detail-screen';
import { WorkRoleScreen } from '../admin/ui/screens/workrole-screen/workrole-screen';
import { LangCode, WithI18nProps } from '../internationalization/i18n-hoc';
import { i18next } from '../internationalization/i18next';
import { Spinner } from '../loader/loader';
import { Route } from '../routing/app-routes';
import { AppStore } from '../stores/app-store';
import { AdminAppState } from "../stores/app-types";
import { LoggedIn, LoggedInSuper, Unregistered } from '../stores/identity-store';
import { LayoutType, MenuLink } from '../ui/common/page-layout/page-layout';
import { ManagerPortalScreen } from '../ui/screens/manager-portal/manager-portal';
import { ManagerResultsScreen } from '../ui/screens/manager-results-screen/manager-results-screen';
import { MyAssessmentsScreen } from '../ui/screens/my-assessments-screen/my-assessments-screen';
import { NotLoggedInScreen } from '../ui/screens/not-logged-in-screen/not-logged-in-screen';
import { QuizQuestionScreen } from '../ui/screens/quiz-question-screen/quiz-question-screen';
import { QuizResultsScreen } from '../ui/screens/quiz-results-screen/quiz-results-screen';
import { ReportsPortalScreen } from '../ui/screens/reports-portal/reports-portal';
import { Select, SelectOption } from '../ui/widgets/select/select';
import { assertNever } from '../util/types';

import {
  APPLICATION_LINK_FACTORY,
  createAdminPageLayout,
  createPageLayout,
  defaultHeaderLinks,
  defaultSidebarLinks
} from './app-pagelayout';
import { TrainingOfferingScreen } from 'src/admin/ui/screens/training-offering-screen/training-offering-screen';

const defaultLogo = require('../assets/images/skillseeker-logo.jpg');

interface AppProps extends WithI18nProps {
  /** Top level app state and actions */
  store: AppStore;
  /** UI configuration */
  uiconfig: UiConfig;
}

@observer
export class App extends React.Component<AppProps> {
  /// TODO: set up a factory for all this? (cf app-pagelayout)
  /// 1. Client list for sidebar
  setLayoutAppClientOptions = (loggedInUser: LoggedIn | LoggedInSuper): SelectOption[] => {
    /// TODO: Make observable and update as necessary (eg. creating new client doesn't update these options autromatically)
    let appClientOptions: SelectOption[] = [];
    if (loggedInUser.kind === 'super-logged-in') {
      appClientOptions = loggedInUser.appClientList.map(client => ({ label: client.value.name, value: client.id }));
    } else if (loggedInUser.role === ApplicationRole.admin) {
      appClientOptions.push({
        label: loggedInUser.appClient.name,
        value: loggedInUser.appUserWithDbId.value.appClientId
      });
    }
    return appClientOptions;
  };
  /// 2. HeaderLinks
  setLayoutHeaderLinks = (state: AdminAppState): MenuLink[] => {
    const { navigateTo } = this.props.store;
    const { page } = state.state;

    switch (page) {
      case 'users':
      case 'groups':
      case 'competencies-skills':
      case 'questions':
      case 'recommendations':
        return [];

      case 'clients':
      case 'client':
      case 'competency':
      case 'group':
      case 'question':
      case 'quiz-admin':
      case 'reports':
      case 'role':
      case 'role-builder':
      case 'roles':
      case 'skill':
      case 'user':
      case 'training-offering':
        return defaultHeaderLinks(state.identity, state, navigateTo, 'ADMIN');
      default:
        assertNever(page, 'Layout Header links not fully defined');
        return [];
      // default: return defaultHeaderLinks(state.identity, state, navigateTo, "ADMIN");
    }
  };
  /// 3. SidebarLinks
  // TODO: This needs testing and sorting out
  setLayoutSidebarLinks = (state: AdminAppState): MenuLink[] => {
    const { navigateTo } = this.props.store;
    const { page, appClientId } = state.state;

    switch (page) {
      case 'clients':
        return defaultSidebarLinks(state.identity, state, navigateTo, 'SUPER_CLIENTS', appClientId === undefined);
      case 'reports':
        return defaultSidebarLinks(state.identity, state, navigateTo, 'REPORTS', appClientId === undefined);
      case 'competencies-skills':
        return defaultSidebarLinks(
          state.identity,
          state,
          navigateTo,
          'SUPER_COMPETENCIES_SKILLS',
          appClientId === undefined
        );
      case 'questions':
        return defaultSidebarLinks(state.identity, state, navigateTo, 'SUPER_QUESTIONS', appClientId === undefined);

      case 'recommendations':
        return defaultSidebarLinks(state.identity, state, navigateTo, 'SUPER_RECOMMENDATIONS', appClientId === undefined);

      case 'client':
        return defaultSidebarLinks(state.identity, state, navigateTo, 'ADMIN_CLIENT', appClientId === undefined);

      case 'users':
        return defaultSidebarLinks(state.identity, state, navigateTo, 'ADMIN_USERS', false);
      case 'groups':
        return defaultSidebarLinks(state.identity, state, navigateTo, 'ADMIN_GROUPS', false);

      case 'roles':
        return defaultSidebarLinks(state.identity, state, navigateTo, 'ADMIN_ROLE_BUILDER', true);

      case 'competency':
      case 'skill':
      case 'role':
      case 'role-builder':
      case 'quiz-admin':
      case 'question':
      case 'training-offering':
        return defaultSidebarLinks(state.identity, state, navigateTo, 'ADMIN', true);

      case 'group':
      case 'user':
        return defaultSidebarLinks(state.identity, state, navigateTo, 'ADMIN', false);
      default:
        assertNever(page, 'SideBar links not fully defined');
        return [];
    }
  };
  /// 4. sidebarSubMenus
  setLayoutSidebarSubMenus = (state: AdminAppState): { [parentLabel: string]: MenuLink[] } => {
    const {
      page
    } = state.state;

    switch (page) {
      default:
        return {};
    }
  };
  /// 4. sidebarSubMenus

  /// 5. onSelectClient nav
  setLayoutOnSelectClient = (state: AdminAppState): ((appClientId: DbKey<AppClient>) => void) | undefined => {
    const { navigateTo } = this.props.store;
    const { page } = state.state;

    switch (page) {
      case 'clients':
      case 'users':
      case 'groups':
      case 'competencies-skills':
      case 'roles':
      case 'reports':
      case 'questions':
      case 'recommendations':
        return appClientId => navigateTo({ route: 'admin', page: 'client', appClientId, id: appClientId });

      case 'client':
      case 'competency':
      case 'group':
      case 'quiz-admin':
      case 'role':
      case 'role-builder':
      case 'skill':
      case 'user':
      case 'question':
      case 'training-offering':
        return undefined;
      default:
        assertNever(page, 'Sidebar AppClient select action not defined');
        return undefined;
    }
  };
  /// 6. pageLayout
  setAdminPageLayout = (state: AdminAppState): AdminPageLayoutFn => {
    const { appClientId, page } = state.state;

    const appClientOptions = this.setLayoutAppClientOptions(state.identity);
    const headerLinks = this.setLayoutHeaderLinks(state);
    const sidebarLinks = this.setLayoutSidebarLinks(state);
    const sidebarSubMenus = this.setLayoutSidebarSubMenus(state);
    const onSelectClient = this.setLayoutOnSelectClient(state);
    let selectedClient: DbKey<AppClient> | undefined;

    switch (page) {
      case 'clients':
        selectedClient = undefined;
        break;
      /// Client - originally for 'new' this was undefined but I don't think it should make any difference
      case 'client':
        {
          const { view, id } = state.state;
          switch (view) {
            /// Undefined => view only no update/new/delete
            case undefined:
              selectedClient = id;
              break;
            default:
              selectedClient = appClientId;
          }
        }
        break;

      /// Client-independent
      case 'competency':
        selectedClient = undefined;
        break;

      case 'competencies-skills':
      case 'group':
      case 'groups':
      case 'quiz-admin':
      case 'reports':
      case 'role':
      case 'role-builder':
      case 'roles':
      case 'skill':
      case 'user':
      case 'users':
      case 'questions':
      case 'question':
      case 'recommendations':
      case 'training-offering':
        selectedClient = appClientId;
        break;
      default:
        assertNever(page, 'AppClientId not defined for layout');
      // /// rest - lists, user, group
      // default:  selectedClient = appClientId
    }

    console.debug('DEBUG Setting pagelayout appClientId, selectedClient', appClientId, selectedClient);
    return this.createAdminPageLayout(
      state.identity,
      headerLinks,
      sidebarLinks,
      appClientOptions,
      sidebarSubMenus,
      selectedClient,
      onSelectClient
    );
  };
  /// 6. OnCancel
  setOnCancel = (state: AdminAppState): (() => Promise<void>) | undefined => {
    const { navigateTo } = this.props.store;
    const { appClientId, page } = state.state;
    const { kind: route } = state;

    /// Belt & braces
    if (route === 'admin') {
      const baseAdminRoute = { route, page, view: undefined, appClientId, id: undefined };

      switch (page) {
        case 'client': {
          const { view, id } = state.state;
          switch (view) {
            case 'new':
              return async () => navigateTo({ ...baseAdminRoute, page: 'clients', appClientId: undefined });
            case 'update':
              return async () => navigateTo({ ...baseAdminRoute, id });
            default:
              return undefined;
          }
        }
        case 'user': {
          const { view, id } = state.state;
          switch (view) {
            case 'new':
              return async () => navigateTo({ ...baseAdminRoute, page: 'users' });
            case 'update':
              return async () => navigateTo({ ...baseAdminRoute, id });
            case 'upload':
              return async () =>
                navigateTo({ route: 'admin', page: 'users', view: undefined, appClientId, id: undefined });
            case 'upload-results':
              return async () =>
                navigateTo({ route: 'admin', page: 'users', view: undefined, appClientId, id: undefined });
            default:
              return undefined;
          }
        }
        case 'group': {
          const { view, id } = state.state;
          switch (view) {
            case 'new':
              return async () => navigateTo({ ...baseAdminRoute, page: 'groups' });
            case 'update':
              return async () => navigateTo({ ...baseAdminRoute, id });
            default:
              return undefined;
          }
        }
        case 'role': {
          const { view, id } = state.state;
          switch (view) {
            case 'new':
              return async () => navigateTo({ ...baseAdminRoute, page: 'roles' });
            case 'update':
              return async () => navigateTo({ ...baseAdminRoute, id });
            /// TODO: Go all the way up to role list cos it's too hard rn to go to the workrole-details-view on the new ID
            case 'clone':
              return async () => navigateTo({ ...baseAdminRoute, page: 'roles' });
            default:
              return undefined;
          }
        }

        case 'role-builder': {
          const { view, id } = state.state;
          switch (view) {
            default:
              return async () => navigateTo({ ...baseAdminRoute, page: 'role', id });
          }
        }

        case 'competency':
        case 'skill': {
          const { view, id } = state.state;
          switch (view) {
            case 'new':
              return async () => navigateTo({ ...baseAdminRoute, page: 'competencies-skills', appClientId: undefined });
            case 'update':
              return async () => navigateTo({ ...baseAdminRoute, appClientId: undefined, id });
            default:
              return undefined;
          }
        }

        case 'quiz-admin': {
          /// We have to throw this guard in here - otherwise we have to add this field to all versions of AdminState
          if ('workRoleId' in state.state) {
            const { view, workRoleId } = state.state;
            switch (view) {
              default:
                return async () => navigateTo({ ...baseAdminRoute, page: 'role', id: workRoleId });
            }
          }
        }

        case 'question': {
          const { id, view } = state.state;
          let skillId: string | undefined;
          /// We have to throw this guard in here - otherwise we have to add this field to all versions of AdminState
          if ('skillId' in state.state) {
            skillId = state.state.skillId;
          }
          switch (view) {
            case 'new':
              return async () => navigateTo({ ...baseAdminRoute, page: 'questions' });
            default:
              return async () => navigateTo({ ...baseAdminRoute, skillId, id });
          }
        }

        case 'training-offering': {
          const { id, view } = state.state;
          switch (view) {
            case 'new':
              return async () => navigateTo({ ...baseAdminRoute, page: 'recommendations' });
            default:
              return async () => navigateTo({ ...baseAdminRoute, id });
          }
        }

        case 'clients':
        case 'competencies-skills':
        case 'groups':
        case 'reports':
        case 'roles':
        case 'users':
        case 'questions':
        case 'recommendations':
          return undefined;
        default:
          assertNever(page, 'OnCancel not set up');
          return undefined;
      }
    } else {
      return undefined;
    }
  };
  /// 7. OnUpdate
  setOnUpdate = (state: AdminAppState): (() => Promise<void>) | undefined => {
    const { navigateTo } = this.props.store;
    const { appClientId, page } = state.state;
    const { kind: route } = state;

    if (route === 'admin') {
      const baseAdminRoute: Route = { route, page, view: 'update', appClientId, id: undefined };
      switch (page) {
        case 'client': {
          const { view } = state.state;
          /// view = Undefined => View only, no update/create/delete
          switch (view) {
            case undefined:
              return async () => navigateTo(baseAdminRoute);
            default:
              return undefined;
          }
        }
        case 'user':
        case 'group': {
          const { view, id } = state.state;
          switch (view) {
            case undefined:
              return async () => navigateTo({ ...baseAdminRoute, id });
            default:
              return undefined;
          }
        }

        case 'role': {
          const { view, id } = state.state;
          switch (view) {
            case undefined:
              return async () => navigateTo({ ...baseAdminRoute, id });
            default:
              return undefined;
          }
        }

        case 'competency':
        case 'skill':
        case 'question':
        case 'training-offering': {
          const { view, id } = state.state;
          switch (view) {
            case undefined:
              return async () => navigateTo({ ...baseAdminRoute, appClientId: undefined, id });
            default:
              return undefined;
          }
        }
        case 'clients':
        case 'competencies-skills':
        case 'groups':
        case 'quiz-admin':
        case 'reports':
        case 'role-builder':
        case 'roles':
        case 'users':
        case 'questions':
        case 'recommendations':
          return undefined;
        default:
          assertNever(page, 'OnUpdate not set up');
          return undefined;
      }
    } else {
      return undefined;
    }
  };

  setOnClone = (state: AdminAppState): (() => Promise<void>) | undefined => {
    const { navigateTo } = this.props.store;
    const { appClientId, page } = state.state;

    /// Belt & braces
    if (state.kind === 'admin') {
      const route = state.kind;
      switch (page) {
        case 'role': {
          const { view, id } = state.state;
          switch (view) {
            // case "update":
            case undefined:
              return async () => navigateTo({ route, page, view: 'clone', appClientId, id });
            default:
              return undefined;
          }
        }
        case 'client':
        case 'clients':
        case 'competencies-skills':
        case 'competency':
        case 'group':
        case 'groups':
        case 'quiz-admin':
        case 'reports':
        case 'role-builder':
        case 'roles':
        case 'skill':
        case 'user':
        case 'users':
        case 'questions':
        case 'question':
        case 'recommendations':
        case 'training-offering':
          return undefined;
        default:
          assertNever(page, 'OnClone not set up');
          return undefined;
      }
    } else {
      return undefined;
    }
  };

  renderAdmin = (state: AdminAppState): JSX.Element | null => {
    const { navigateTo } = this.props.store;

    const adminPageLayout = this.setAdminPageLayout(state);
    const onCancel = this.setOnCancel(state);
    const onUpdate = this.setOnUpdate(state);
    const onClone = this.setOnClone(state);

    const adminState = state.state;

    // tslint:disable-next-line: no-console
    console.info('app render page=', adminState.page);
    // tslint:disable-next-line: no-console
    console.info('app render view=', adminState.view);
    switch (adminState.page) {
      case 'clients': {
        return (
          <AppClientScreen
            pageLayout={adminPageLayout}
            navigateTo={navigateTo}
            clients={adminState.store.loadingList}
          />
        );
      }

      case 'users': {
        return (
          <AppUserScreen
            pageLayout={adminPageLayout}
            navigateTo={navigateTo}
            users={adminState.store.loadingList}
            userData={adminState.store.loadingData}
          />
        );
      }

      case 'groups': {
        return (
          <UserGroupScreen
            pageLayout={adminPageLayout}
            navigateTo={navigateTo}
            groups={adminState.store.loadingList}
            groupData={adminState.store.loadingData}
          />
        );
      }

      case 'competencies-skills': {
        return (
          <CompetencySkillScreen
            pageLayout={adminPageLayout}
            navigateTo={navigateTo}
            competencies={adminState.store.loadingList}
          />
        );
      }

      case 'roles': {
        return (
          <WorkRoleScreen
            pageLayout={adminPageLayout}
            navigateTo={navigateTo}
            appClientId={adminState.appClientId}
            roles={adminState.store.loadingList}
            roleData={adminState.store.loadingData}
          />
        );
      }

      case 'questions': {
        return (
          <QuestionScreen
            pageLayout={adminPageLayout}
            navigateTo={navigateTo}
            questions={adminState.store.loadingList}
            competencySkillsMap={adminState.store.loadingCompetencySkillsMap}
          />
        );
      }

      case 'recommendations': {
        return (
          <TrainingOfferingScreen
            pageLayout={adminPageLayout}
            navigateTo={navigateTo}
            data={adminState.store.loadingAll}
          />
        );
      }

      case 'client': {
        // tslint:disable-next-line: no-console
        console.info('app render view=', adminState.view);
        if (adminState.view !== undefined || adminState.id) {
          return (
            <AppClientDetailScreen
              pageLayout={adminPageLayout}
              onUpdate={onUpdate}
              onCancel={onCancel}
              /// TODO: Delete
              mode={adminState.view === 'new' ? 'create' : adminState.view === 'update' ? 'update' : undefined}
              store={adminState.store}
            />
          );
        } else {
          return null;
        }
      }

      case 'user': {
        if (adminState.view === 'upload') {
          return (
            <AppUserUploadScreen
              pageLayout={adminPageLayout}
              client={(adminState.store as AppUserUploadStore).loadingClient}
              store={adminState.store as AppUserUploadStore}
              onCancel={onCancel}
              onUpload={async (data: UserUploadData) => {
                const result = await (adminState.store as AppUserUploadStore).processUpload(data);
                console.debug('DEBUG result kind, set ', result.result.kind, result.set);
                this.props.store.navigateTo({
                  route: 'admin',
                  page: 'user',
                  view: 'upload-results',
                  appClientId: adminState.appClientId,
                  id: result.set
                });
              }}
            />
          );
        } else if (adminState.view === 'upload-results') {
          return (
            <AppUserUploadResultScreen
              pageLayout={adminPageLayout}
              store={adminState.store as AppUserUploadStore}
              data={(adminState.store as AppUserUploadStore).loadingResult}
              onCancel={onCancel}
            />
          );
        } else if (adminState.view !== undefined || adminState.id) {
          return (
            <AppUserDetailScreen
              pageLayout={adminPageLayout}
              onUpdate={onUpdate}
              onCancel={onCancel}
              /// TODO: Delete
              mode={adminState.view === 'new' ? 'create' : adminState.view === 'update' ? 'update' : undefined}
              store={adminState.store as AppUserStore}
            />
          );
        } else {
          return null;
        }
      }

      case 'group': {
        if (adminState.view !== undefined || adminState.id) {
          return (
            <UserGroupDetailsScreen
              pageLayout={adminPageLayout}
              onUpdate={onUpdate}
              onCancel={onCancel}
              /// TODO: Delete
              mode={adminState.view === 'new' ? 'create' : adminState.view === 'update' ? 'update' : undefined}
              store={adminState.store}
            />
          );
        } else {
          return null;
        }
      }

      case 'competency': {
        if (adminState.view !== undefined || adminState.id) {
          return (
            <CompetencyDetailScreen
              pageLayout={adminPageLayout}
              navigateTo={navigateTo}
              onUpdate={onUpdate}
              onCancel={onCancel}
              /// TODO: Delete
              mode={adminState.view === 'new' ? 'create' : adminState.view === 'update' ? 'update' : undefined}
              store={adminState.store}
            />
          );
        } else {
          return null;
        }
      }
      case 'skill': {
        if (adminState.view !== undefined || adminState.id) {
          return (
            <SkillDetailScreen
              pageLayout={adminPageLayout}
              navigateTo={navigateTo}
              onUpdate={onUpdate}
              onCancel={onCancel}
              /// TODO: Delete
              mode={adminState.view === 'new' ? 'create' : adminState.view === 'update' ? 'update' : undefined}
              store={adminState.store}
            />
          );
        } else {
          return null;
        }
      }
      case 'role': {
        const { id, store, view } = adminState;
        if (id === undefined) {
          if (view === 'new') {
            const mode: FormMode = 'create';
            return (
              <WorkRoleDetailScreen
                pageLayout={adminPageLayout}
                navigateTo={navigateTo}
                onUpdate={onUpdate}
                onCancel={onCancel}
                /// TODO: Delete
                mode={mode}
                store={store}
              />
            );
          }
          return null;
        } else {
          const mode: FormMode | undefined = view === 'update' ? 'update' : view === 'clone' ? 'clone' : undefined;
          const entry = store.selectedRole;
          if (entry && entry.kind === 'value') {
            return (
              <WorkRoleDetailScreen
                // data={store.loadingRoleBuilderData}
                selectedRole={entry.value}
                pageLayout={adminPageLayout}
                navigateTo={navigateTo}
                onUpdate={onUpdate}
                onCancel={onCancel}
                onCloneWorkRole={onClone}
                /// TODO: Delete
                mode={mode}
                store={store}
              />
            );
          } else {
            return null;
          }
        }
      }
      case 'role-builder': {
        if (adminState.view !== undefined || adminState.id) {
          const entry = adminState.store.selectedRole;
          if (entry && entry.kind === 'value') {
            return (
              <RoleBuilderScreen
                pageLayout={adminPageLayout}
                navigateTo={navigateTo}
                data={adminState.store.loadingRoleBuilderData}
                roleReferenceData={adminState.store.loadingData}
                store={adminState.store}
                selectedRole={entry.value}
                onSelectRole={adminState.store.setSelectedRole}
                // onUpdate={onUpdate}
                onCancel={onCancel}
              // /// TODO: Delete
              // mode={adminState.view === "new" ? "create" : adminState.view === "update" ? "update" : undefined}
              />
            );
          } else {
            return null;
          }
        } else {
          return null;
        }
      }
      case 'quiz-admin': {
        if (adminState.view === 'new' && adminState.workRoleId) {
          return (
            <QuizAdminScreen
              pageLayout={adminPageLayout}
              navigateTo={navigateTo}
              workrole={adminState.store.loadingWorkRole}
              repository={adminState.store.loadingRepository}
              nonSkillQuestions={adminState.store.loadingNonSkillQuestions}
              store={adminState.store}
              onCancel={onCancel}
              /// TODO: Update/Delete
              mode={adminState.view === 'new' ? 'create' : undefined}
            />
          );
        } else {
          return null;
        }
      }

      case 'reports': {
        return (
          <ReportsPortalScreen
            pageLayout={adminPageLayout}
            reports={adminState.store.loading}
            navigateTo={this.props.store.navigateTo}
            identity={state.identity}
            onClickReport={adminState.store.generateReport}
          />
        );
      }

      case 'question': {
        if (adminState.view !== undefined || adminState.id) {
          return (
            <QuestionAnswerDetailScreen
              pageLayout={adminPageLayout}
              repository={adminState.store.loadingRepository}
              competencySkillsMap={adminState.store.loadingCompetencySkillsMap}
              store={adminState.store}
              onUpdate={onUpdate}
              onCancel={onCancel}
              /// TODO: Update/Delete
              mode={adminState.view === 'new' ? 'create' : adminState.view === 'update' ? 'update' : undefined}
            />
          );
        } else {
          return null;
        }
      }

      case 'training-offering': {
        if (adminState.view !== undefined || adminState.id) {
          // This is batshit insane - we have to actually pick up the selectedEntry to make this work otherwise it's lost in a promise till later
          return (
            <TrainingOfferingDetailScreen
              pageLayout={adminPageLayout}
              navigateTo={navigateTo}
              store={adminState.store}
              selectedEntry={adminState.store.selectedEntry}
              onUpdate={onUpdate}
              onCancel={onCancel}
              /// TODO: Update/Delete
              mode={adminState.view === 'new' ? 'create' : adminState.view === 'update' ? 'update' : undefined}
            />
          );
        } else {
          return null;
        }
      }

      default:
        assertNever(adminState, 'Unknown state');
        return <div>Unknown state</div>;
    } // switch page
  };

  // class App0 extends React.Component<AppProps> {
  render() {
    const state = this.props.store.state;

    // tslint:disable-next-line: no-console
    console.info('app render state=', state.kind);

    switch (state.kind) {
      case 'identity-loading':
        // Usually internal to <Loader> but in this app we have unpacked the identity loader
        // so that it has typesafe state.identity fields
        return <Spinner />;
      case 'identity-not-logged-in':
        // the old protoapp home screen
        return (
          <NotLoggedInScreen
            logoUrl={this.getLogoUrl(this.props.uiconfig)}
            onClickLogin={async () => {
              return this.props.store.auth.login('app');
            }}
          ></NotLoggedInScreen>
        );
      case 'identity-unregistered':
        const PageLayout = this.createPageLayout(state.identity, []);
        return <PageLayout>{notEnabledToDisplayString(state.identity.reason)}</PageLayout>;
      case 'identity-error':
        return <div>There was an error loading your user identity</div>;
      case 'my-assessments': {
        const headerLinks: MenuLink[] = defaultHeaderLinks(
          state.identity,
          state,
          this.props.store.navigateTo,
          'MY_ASSESSMENTS'
        );
        return (
          <MyAssessmentsScreen
            pageLayout={this.createPageLayout(state.identity, headerLinks)}
            identity={state.identity}
            userHomeData={state.userQuizDataStore.data}
            navigateTo={this.props.store.navigateTo}
            onClickQuiz={(
              quizId: DbKey<Quiz>,
              userQuizId: DbKey<UserQuiz>,
              status: AssessmentStatus,
              quizType: QuizType
            ) => {
              if (status.progress >= status.total) {
                this.props.store.navigateTo({
                  route: 'quiz-results',
                  quizId,
                  userQuizId,
                  page: 'profile'
                });
              } else {
                quizType === QuizType.selfAssessment
                  ? this.props.store.navigateTo({ route: 'self-assessment', userQuizId })
                  : this.props.store.navigateTo({ route: 'quiz', userQuizId });
              }
            }}
            walkThroughFn={state.walkThroughFn}
          ></MyAssessmentsScreen>
        );
      }
      case 'quiz': {
        const headerLinks: MenuLink[] = defaultHeaderLinks(
          state.identity,
          state,
          this.props.store.navigateTo,
          'MY_ASSESSMENTS'
        );
        return (
          <QuizQuestionScreen
            pageLayout={this.createPageLayout(state.identity, headerLinks)}
            userQuizSummary={state.quizStore.userQuizSummary}
            navigateTo={this.props.store.navigateTo}
            nextQuestion={state.quizStore.nextQuestion}
            onHeartbeat={state.quizStore.heartbeat}
            onSelect={state.quizStore.selectAnswer}
            onNext={state.quizStore.submitAnswer}
            onDone={async () => {
              const summary = await state.quizStore.getUserQuizSummary();
              await state.quizStore.recreateUserQuiz();
              return summary.lockResults
                ? this.props.store.navigateTo({
                  route: 'home'
                })
                : this.props.store.navigateTo({
                  route: 'quiz-results',
                  quizId: summary.quizId,
                  userQuizId: state.quizStore.userQuizId,
                  page: 'profile'
                });
            }}
            getEmbeddedCourseInfo={state.quizStore.getCourseInfoWithTrackedLink}
            onGenerateCompletionEvent={state.quizStore.generateCompletionEvent}
            onFetchCompletionEvent={state.quizStore.fetchCompletionEvent}
          ></QuizQuestionScreen>
        );
      }
      case 'self-assessment': {
        const headerLinks: MenuLink[] = defaultHeaderLinks(
          state.identity,
          state,
          this.props.store.navigateTo,
          'MY_ASSESSMENTS'
        );
        return (
          <SelfAssessmentQuestionScreen
            pageLayout={this.createPageLayout(state.identity, headerLinks)}
            userQuizSummary={state.quizStore.userQuizSummary}
            navigateTo={this.props.store.navigateTo}
            nextQuestion={state.quizStore.nextQuestion}
            workRole={state.quizStore.workRoleListEntry}
            onHeartbeat={state.quizStore.heartbeat}
            onSelect={state.quizStore.selectAnswer}
            onNext={state.quizStore.submitSelfAssessmentAnswers}
            onDone={async () => {
              const summary = await state.quizStore.finishSelfAssessment();
              await state.quizStore.recreateUserQuiz();
              return summary.lockResults
                ? this.props.store.navigateTo({
                  route: 'home'
                })
                : this.props.store.navigateTo({
                  route: 'quiz-results',
                  quizId: summary.quizId,
                  userQuizId: state.quizStore.userQuizId,
                  page: 'profile'
                });
            }}
          ></SelfAssessmentQuestionScreen>
        );
      }
      case 'quiz-results': {
        const headerLinks: MenuLink[] = defaultHeaderLinks(
          state.identity,
          state,
          this.props.store.navigateTo,
          'MY_ASSESSMENTS'
        );
        const subnavLinks: MenuLink[] = [
          APPLICATION_LINK_FACTORY.QUIZ_RESULTS_PROFILE(state, this.props.store.navigateTo, state.page === 'profile'),
          APPLICATION_LINK_FACTORY.QUIZ_RESULTS_GUIDANCE(state, this.props.store.navigateTo, state.page === 'guidance'),
          APPLICATION_LINK_FACTORY.QUIZ_RESULTS_RECOMMENDATIONS(
            state,
            this.props.store.navigateTo,
            state.page === 'recommendations'
          )
        ];
        const layoutType: LayoutType =
          state.page === 'recommendations' && !state.identity.appClient.singleTabRecommendations
            ? 'tabbedContentUi'
            : 'singlePageUi';

        const settings = state.quizResultsStore.getSettings();
        if (settings) {
          settings.guidanceAvailable = state.quizResultsStore.guidanceAvailable;
          settings.recommendationsAvailable = state.quizResultsStore.recommendationsAvailable;
        }

        return (
          <QuizResultsScreen
            pageLayout={this.createPageLayout(
              state.identity,
              headerLinks,
              subnavLinks,
              undefined,
              state.quizResultsStore.categoryFilterApplied,
              state.quizResultsStore.competencyFilterApplied,
              state.quizResultsStore.priorityFilterApplied,
              layoutType
            )}
            page={state.page}
            identity={state.identity}
            navigateTo={this.props.store.navigateTo}
            userQuizId={state.quizResultsStore.userQuizId}
            propsToLoad={state.quizResultsStore.propsToLoad}
            plotKeyToggleState={state.plotKeyToggleState}
            selectRole={selectedRole => {
              this.props.store.navigateTo({
                route: 'quiz-results',
                quizId: state.quizResultsStore.quizId,
                userQuizId: state.quizResultsStore.userQuizId,
                page: state.page,
                settingsJson: JSON.stringify({
                  ...settings,
                  role: selectedRole
                })
              });
            }}
            selectCategories={selected => {
              this.props.store.navigateTo({
                route: 'quiz-results',
                quizId: state.quizResultsStore.quizId,
                userQuizId: state.quizResultsStore.userQuizId,
                page: state.page,
                settingsJson: JSON.stringify({
                  ...settings,
                  categories: selected
                })
              });
            }}
            selectCompetencies={selected => {
              this.props.store.navigateTo({
                route: 'quiz-results',
                quizId: state.quizResultsStore.quizId,
                userQuizId: state.quizResultsStore.userQuizId,
                page: state.page,
                settingsJson: JSON.stringify({
                  ...settings,
                  competencies: selected
                })
              });
            }}
            selectPriorities={selected => {
              this.props.store.navigateTo({
                route: 'quiz-results',
                quizId: state.quizResultsStore.quizId,
                userQuizId: state.quizResultsStore.userQuizId,
                page: state.page,
                settingsJson: JSON.stringify({
                  ...settings,
                  priorities: selected
                })
              });
            }}
            resetAll={() =>
              this.props.store.navigateTo({
                route: 'quiz-results',
                quizId: state.quizResultsStore.quizId,
                userQuizId: state.quizResultsStore.userQuizId,
                page: state.page,
                settingsJson: JSON.stringify({
                  ...settings,
                  role: undefined,
                  categories: undefined,
                  competencies: undefined,
                  priorities: undefined
                })
              })
            }
            walkThroughFn={state.walkThroughFn}
            walkThroughMgr={state.walkThroughMgr}
            onLaunchTrainingOffering={state.quizResultsStore.getTrainingOfferingUrl}
          ></QuizResultsScreen>
        );
      }
      case 'manager': {
        const headerLinks: MenuLink[] = defaultHeaderLinks(
          state.identity,
          state,
          this.props.store.navigateTo,
          'MANAGER_REPORTS'
        );
        return (
          <ManagerPortalScreen
            pageLayout={this.createPageLayout(state.identity, headerLinks)}
            quizReportSummaries={state.managerPortalStore.loading}
            navigateTo={this.props.store.navigateTo}
            onClickQuiz={quizId => {
              this.props.store.navigateTo({
                route: 'manager-quiz-results',
                quizId,
                page: 'profile'
              });
            }}
          />
        );
      }
      case 'manager-quiz-results': {
        const headerLinks: MenuLink[] = defaultHeaderLinks(
          state.identity,
          state,
          this.props.store.navigateTo,
          'MANAGER_REPORTS'
        );
        const subnavLinks: MenuLink[] = [
          APPLICATION_LINK_FACTORY.MANAGER_RESULTS_PROFILE(
            state,
            this.props.store.navigateTo,
            state.page === 'profile'
          ),
          APPLICATION_LINK_FACTORY.MANAGER_RESULTS_GUIDANCE(
            state,
            this.props.store.navigateTo,
            state.page === 'guidance'
          ),
          APPLICATION_LINK_FACTORY.MANAGER_RESULTS_RECOMMENDATIONS(
            state,
            this.props.store.navigateTo,
            state.page === 'recommendations'
          )
        ];
        const layoutType: LayoutType =
          state.page === 'recommendations' && !state.identity.appClient.singleTabRecommendations
            ? 'tabbedContentUi'
            : 'singlePageUi';
        return (
          <ManagerResultsScreen
            page={state.page}
            identity={state.identity}
            pageLayout={this.createPageLayout(
              state.identity,
              headerLinks,
              subnavLinks,
              state.managerQuizResultsStore.filterApplied,
              state.managerQuizResultsStore.categoryFilterApplied,
              state.managerQuizResultsStore.competencyFilterApplied,
              state.managerQuizResultsStore.priorityFilterApplied,
              layoutType
            )}
            loading={state.managerQuizResultsStore.loading}
            applyFilter={filtersSelected => {
              this.props.store.navigateTo({
                route: 'manager-quiz-results',
                quizId: state.managerQuizResultsStore.quizId,
                page: state.page,
                settingsJson: JSON.stringify({
                  ...state.managerQuizResultsStore.getSettings(),
                  filters: filtersSelected
                })
              });
            }}
            selectRole={selectedRole => {
              this.props.store.navigateTo({
                route: 'manager-quiz-results',
                quizId: state.managerQuizResultsStore.quizId,
                page: state.page,
                settingsJson: JSON.stringify({
                  ...state.managerQuizResultsStore.getSettings(),
                  role: selectedRole
                })
              });
            }}
            selectCategories={selected => {
              this.props.store.navigateTo({
                route: 'manager-quiz-results',
                quizId: state.managerQuizResultsStore.quizId,
                page: state.page,
                settingsJson: JSON.stringify({
                  ...state.managerQuizResultsStore.getSettings(),
                  categories: selected
                })
              });
            }}
            selectCompetencies={selected => {
              this.props.store.navigateTo({
                route: 'manager-quiz-results',
                quizId: state.managerQuizResultsStore.quizId,
                page: state.page,
                settingsJson: JSON.stringify({
                  ...state.managerQuizResultsStore.getSettings(),
                  competencies: selected
                })
              });
            }}
            selectPriorities={selected => {
              this.props.store.navigateTo({
                route: 'manager-quiz-results',
                quizId: state.managerQuizResultsStore.quizId,
                page: state.page,
                settingsJson: JSON.stringify({
                  ...state.managerQuizResultsStore.getSettings(),
                  priorities: selected
                })
              });
            }}
            resetAll={() =>
              this.props.store.navigateTo({
                route: 'manager-quiz-results',
                quizId: state.managerQuizResultsStore.quizId,
                page: state.page,
                settingsJson: JSON.stringify({
                  ...state.managerQuizResultsStore.getSettings(),
                  filters: undefined,
                  role: undefined,
                  categories: undefined,
                  competencies: undefined,
                  priorities: undefined
                })
              })
            }
            plotKeyToggleState={state.plotKeyToggleState}
            walkThroughFn={state.walkThroughFn}
            walkThroughMgr={state.walkThroughMgr}
            onLaunchTrainingOffering={state.managerQuizResultsStore.getTrainingOfferingUrl}
          />
        );
      }

      case 'admin':
        return this.renderAdmin(state);

      /// End of admin pages

      default:
        assertNever(state, 'unknown state');
        return <div>Unknown state</div>;
    }
  }

  createPageLayout(
    identity: LoggedIn | Unregistered,
    headerLinks: MenuLink[],
    subLinks?: MenuLink[],
    filterApplied?: boolean,
    categoryFilterApplied?: boolean,
    competencyFilterApplied?: boolean,
    priorityFilterApplied?: boolean,
    layoutType?: LayoutType
  ) {
    return createPageLayout({
      logoUrl: this.getLogoUrl(this.props.uiconfig),
      userFullName:
        identity.kind === 'logged-in'
          ? identity.appUserWithDbId.value.fullname
          : i18next.t('not-registered.unregistered', 'Unregistered User'),
      logout: () => this.props.store.navigateTo({ route: 'logout' }),
      applicationRole: identity.kind === 'logged-in' ? identity.role : ApplicationRole.user,
      appClientOptions: [],
      layoutType,
      headerLinks,
      subLinks,
      filterApplied,
      categoryFilterApplied,
      competencyFilterApplied,
      priorityFilterApplied,
      langSelector: this.langSelector()
    });
  }

  createAdminPageLayout(
    identity: LoggedIn | LoggedInSuper | Unregistered,
    headerLinks: MenuLink[],
    sidebarLinks: MenuLink[],
    appClientOptions: SelectOption[],
    sidebarSubMenus?: { [parentLabel: string]: MenuLink[] },
    selectedClient?: DbKey<AppClient>,
    onSelectClient?: (appClientId: DbKey<AppClient>) => void
  ) {
    return createAdminPageLayout({
      logoUrl: this.getLogoUrl(this.props.uiconfig),
      userFullName:
        identity.kind === 'logged-in' || identity.kind === 'super-logged-in'
          ? identity.appUserWithDbId.value.fullname
          : i18next.t('not-registered.unregistered', 'Unregistered User'),
      logout: () => this.props.store.navigateTo({ route: 'logout' }),
      role:
        identity.kind === 'logged-in'
          ? identity.role
          : identity.kind === 'super-logged-in'
            ? ApplicationRole.super
            : ApplicationRole.user,
      headerLinks,
      sidebarLinks,
      sidebarSubMenus,
      appClientOptions,
      selectedClient,
      onSelectClient
    });
  }

  langSelector = (): JSX.Element => {
    const { changeLanguage, i18nOptions, currentLangCode } = this.props;
    const onChange = async (opt: SelectOption) => {
      await changeLanguage(opt.value as LangCode);
    };
    const options = i18nOptions();
    return (
      <Select
        options={options}
        defaultValue={options.find(option => (option.value as string) === currentLangCode())}
        onChange={onChange}
      />
    );
  };

  getLogoUrl(uiconfig: UiConfig): string {
    return uiconfig.appClient.logoUrl === '' ? defaultLogo : uiconfig.appClient.logoUrl;
  }
}

function notEnabledToDisplayString(notEnabled: NotEnabled): string {
  switch (notEnabled) {
    case NotEnabled.noUserExists:
      return i18next.t('not-enabled.no-user-exists', 'This user cannot be found - please contact your system admin');
    case NotEnabled.unVerified:
      return i18next.t('not-enabled.user-unverified', "This user hasn't been verified - please check your email");
    case NotEnabled.suspended:
      return i18next.t(
        'not-enabled.user-suspended',
        'User account is disabled. Please contact your system administrator.'
      );
    default:
      throw Error('Unrecognised unregistered state');
  }
}



// WEBPACK FOOTER //
// ./src/app/app.tsx